import { lazy } from 'react';

// project imports
import Loadable from 'components/Loadable';
import DashboardLayout from 'layout/Dashboard';
import ProtectedRoute from 'components/ProtectedRoute';
import RoleGuard from 'components/RoleGuard';

// render- Dashboard
const DashboardDefault = Loadable(lazy(() => import('pages/dashboard/default')));

// render - Projects
const ProjectList = Loadable(lazy(() => import('pages/projects/ProjectList')));
const ProjectForm = Loadable(lazy(() => import('pages/projects/ProjectForm')));
const ProjectDetails = Loadable(lazy(() => import('pages/projects/ProjectDetails')));

// render - Loggers
const LoggerList = Loadable(lazy(() => import('pages/loggers/LoggerList')));
const LoggerForm = Loadable(lazy(() => import('pages/loggers/LoggerForm')));

// render - Building
const BuildingTree = Loadable(lazy(() => import('pages/building/BuildingTree')));

// render - Meter Types
const MeterTypeList = Loadable(lazy(() => import('pages/meter-types/MeterTypeList')));
const MeterTypeForm = Loadable(lazy(() => import('pages/meter-types/MeterTypeForm')));

// render - Measure Units
const MeasureUnitList = Loadable(lazy(() => import('pages/measure-units/MeasureUnitList')));

// render - Meters
const MeterForm = Loadable(lazy(() => import('pages/meters/MeterForm')));

// render - Commissioning
const CommissioningSummary = Loadable(lazy(() => import('pages/commissioning/CommissioningSummary')));

// render - Reports
const ReportsPage = Loadable(lazy(() => import('pages/reports/ReportsPage')));

// render - Schedules
const SchedulesPage = Loadable(lazy(() => import('pages/schedule/SchedulePage')));

// render - Attendances
const AttendancesPage = Loadable(lazy(() => import('pages/attendance/AttendancePage')));

// render - Users
const UserList = Loadable(lazy(() => import('pages/users/UserList')));
const UserForm = Loadable(lazy(() => import('pages/users/UserForm')));

// render - Profile
const EditProfile = Loadable(lazy(() => import('pages/profile/EditProfile')));

// render - Admin pages
const PendingApprovals = Loadable(lazy(() => import('pages/admin/PendingApprovals')));
const ControlCenter = Loadable(lazy(() => import('pages/admin/ControlCenter')));

// render - Subscription pages
const PricingPage = Loadable(lazy(() => import('pages/subscription/PricingPage')));
const BillingPage = Loadable(lazy(() => import('pages/subscription/BillingPage')));

// render - Finance pages
const FinanceOverview = Loadable(lazy(() => import('pages/finance/FinanceOverview')));
const FinanceTransactions = Loadable(lazy(() => import('pages/finance/FinanceTransactions')));
const FinanceCategories = Loadable(lazy(() => import('pages/finance/FinanceCategories')));
const FinanceCostCentres = Loadable(lazy(() => import('pages/finance/FinanceCostCentres')));
const FinanceReports = Loadable(lazy(() => import('pages/finance/FinanceReports')));

// render - Legal pages
const AboutUs = Loadable(lazy(() => import('pages/legal/AboutUs')));
const Privacy = Loadable(lazy(() => import('pages/legal/Privacy')));
const Terms = Loadable(lazy(() => import('pages/legal/Terms')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: (
    <ProtectedRoute>
      <DashboardLayout />
    </ProtectedRoute>
  ),
  children: [
    {
      path: '',
      element: <DashboardDefault />
    },
    {
      path: 'dashboard',
      children: [
        {
          path: 'default',
          element: <DashboardDefault />
        }
      ]
    },
    // Projects
    {
      path: 'projects',
      children: [
        {
          path: '',
          element: <ProjectList />
        },
        {
          path: 'new',
          element: <ProjectForm />
        },
        {
          path: ':id',
          element: <ProjectDetails />
        },
        {
          path: ':id/edit',
          element: <ProjectForm />
        }
      ]
    },
    // Schedules
    {
      path: 'schedules',
      element: <SchedulesPage />
    },
    // Attendances
    {
      path: 'attendances',
      element: <AttendancesPage />
    },
    // Loggers
    {
      path: 'loggers',
      children: [
        {
          path: '',
          element: <LoggerList />
        },
        {
          path: 'new',
          element: <LoggerForm />
        },
        {
          path: ':id/edit',
          element: <LoggerForm />
        }
      ]
    },
    // Building Structure
    {
      path: 'building',
      element: <BuildingTree />
    },
    // Meter Types
    {
      path: 'meter-types',
      children: [
        {
          path: '',
          element: <MeterTypeList />
        },
        {
          path: 'new',
          element: <MeterTypeForm />
        },
        {
          path: ':id/edit',
          element: <MeterTypeForm />
        }
      ]
    },
    // Measure Units
    {
      path: 'measure-units',
      element: <MeasureUnitList />
    },
    // Meters (accessed from Building Structure)
    {
      path: 'meters',
      children: [
        {
          path: 'new',
          element: <MeterForm />
        },
        {
          path: ':id/edit',
          element: <MeterForm />
        }
      ]
    },
    // Commissioning
    {
      path: 'commissioning',
      element: <CommissioningSummary />
    },
    // Reports
    {
      path: 'reports',
      element: <ReportsPage />
    },
    // Users (Admin only)
    {
      path: 'users',
      children: [
        {
          path: '',
          element: <UserList />
        },
        {
          path: 'new',
          element: <UserForm />
        },
        {
          path: ':id/edit',
          element: <UserForm />
        }
      ]
    },
    // Admin - Super Admin only
    {
      path: 'admin',
      children: [
        {
          path: 'pending-approvals',
          element: <PendingApprovals />
        },
        {
          path: 'control-center',
          element: <RoleGuard roles={['SUPER_ADMIN']}><ControlCenter /></RoleGuard>
        }
      ]
    },
    // Subscription
    {
      path: 'subscription',
      children: [
        {
          path: 'pricing',
          element: <RoleGuard roles={['SUPER_ADMIN', 'ORG_ADMIN']}><PricingPage /></RoleGuard>
        },
        {
          path: 'billing',
          element: <RoleGuard roles={['SUPER_ADMIN', 'ORG_ADMIN']}><BillingPage /></RoleGuard>
        }
      ]
    },
    // Finance - Super Admin only
    {
      path: 'finance',
      children: [
        {
          path: 'overview',
          element: <RoleGuard roles={['SUPER_ADMIN']}><FinanceOverview /></RoleGuard>
        },
        {
          path: 'transactions',
          element: <RoleGuard roles={['SUPER_ADMIN']}><FinanceTransactions /></RoleGuard>
        },
        {
          path: 'categories',
          element: <RoleGuard roles={['SUPER_ADMIN']}><FinanceCategories /></RoleGuard>
        },
        {
          path: 'cost-centres',
          element: <RoleGuard roles={['SUPER_ADMIN']}><FinanceCostCentres /></RoleGuard>
        },
        {
          path: 'reports',
          element: <RoleGuard roles={['SUPER_ADMIN']}><FinanceReports /></RoleGuard>
        }
      ]
    },
    // Profile
    {
      path: 'profile',
      children: [
        {
          path: 'edit',
          element: <EditProfile />
        }
      ]
    },
    // Legal pages
    {
      path: 'about',
      element: <AboutUs />
    },
    {
      path: 'privacy',
      element: <Privacy />
    },
    {
      path: 'terms',
      element: <Terms />
    }
  ]
};

export default MainRoutes;
